import { useState } from 'react';
import { ViewIcon, CopyIcon, EditIcon, AddIcon, DeleteIcon } from '@entur/icons';
import { SecondaryButton, PrimaryButton, NegativeButton } from '@entur/button';
import { useIntl } from 'react-intl';
import { Paragraph } from '@entur/typography';
import styles from './styles.module.scss';

interface ToolBarProps {
  editButtonText?: string;
  readonly: boolean;
  setReadonly: (value: boolean) => void;
  createButtonText?: string;
  createButtonLink?: string;
  deleteButtonText?: string;
  deleteButtonLoading?: boolean;
  onClickDeleteButton?: () => void;
  copyButtonText?: string;
  onClickCopyButton?: () => void;
  copyToClipboard?: string;
  onClickCopyToClipboard?: () => void;
  duplicate?: boolean;
}

const ToolBar = ({
  editButtonText,
  readonly = false,
  setReadonly,
  createButtonText,
  createButtonLink,
  deleteButtonText,
  deleteButtonLoading,
  onClickDeleteButton,
  copyButtonText,
  onClickCopyButton = () => {},
  copyToClipboard,
  onClickCopyToClipboard = () => {},
  duplicate = false,
}: ToolBarProps) => {
  const intl = useIntl();

  const [modeText, setModeText] = useState<string>();
  const [showIcon, setShowIcon] = useState<React.ReactNode>();

  return (
    <div className={styles.toprow}>
      {readonly ? (
        <>
          <div>
            <Paragraph className={styles.labelText}>
              <ViewIcon className={styles.labelIcon} />
              {intl.formatMessage({ id: 'generalValues.displayMode' })}
            </Paragraph>
          </div>
          <div className={styles.buttonItems}>
            {deleteButtonText && onClickDeleteButton && (
              <NegativeButton
                onClick={onClickDeleteButton}
                loading={deleteButtonLoading}
                disabled={deleteButtonLoading}
              >
                <DeleteIcon />
                {deleteButtonText}
              </NegativeButton>
            )}
            {copyButtonText &&
              (!createButtonText ? (
                <SecondaryButton
                  onClick={() => {
                    onClickCopyButton();
                    setModeText(intl.formatMessage({ id: 'generalValues.copyMode' }));
                    setShowIcon(<CopyIcon className={styles.labelIcon} />);
                  }}
                >
                  <CopyIcon className={styles.buttonIcon} />
                  {readonly &&
                    !duplicate &&
                    intl.formatMessage({ id: 'generalValues.copyX' }, { value: copyButtonText })}
                  {duplicate && copyButtonText}
                </SecondaryButton>
              ) : (
                <SecondaryButton
                  onClick={() => {
                    onClickCopyButton();
                    setModeText(intl.formatMessage({ id: 'generalValues.copyMode' }));
                    setShowIcon(<CopyIcon className={styles.labelIcon} />);
                  }}
                >
                  <CopyIcon className={styles.buttonIcon} />
                  {readonly && intl.formatMessage({ id: 'generalValues.copyX' }, { value: copyButtonText })}
                </SecondaryButton>
              ))}
            {copyToClipboard && (
              <SecondaryButton
                onClick={() => {
                  onClickCopyToClipboard();
                  setModeText(intl.formatMessage({ id: 'generalValues.copyMode' }));
                  setShowIcon(<CopyIcon className={styles.labelIcon} />);
                }}
              >
                <CopyIcon className={styles.buttonIcon} />
                {readonly && copyToClipboard}
              </SecondaryButton>
            )}
            {editButtonText && (
              <PrimaryButton
                onClick={() => {
                  setReadonly(false);
                  setModeText(intl.formatMessage({ id: 'generalValues.editMode' }));
                  setShowIcon(<EditIcon className={styles.labelIcon} />);
                }}
              >
                <EditIcon className={styles.buttonIcon} />
                {readonly && intl.formatMessage({ id: 'generalValues.editX' }, { value: editButtonText })}
              </PrimaryButton>
            )}
            {createButtonText && createButtonLink && (
              <PrimaryButton className={styles.createButton} as="a" href={createButtonLink}>
                <AddIcon />
                {createButtonText}
              </PrimaryButton>
            )}
          </div>
        </>
      ) : (
        <div className={styles.editMode}>
          <Paragraph className={styles.editText}>
            <span className={styles.editText}>
              {showIcon ?? <EditIcon className={styles.labelIcon} />}
              {modeText ?? intl.formatMessage({ id: 'generalValues.editMode' })}
            </span>
          </Paragraph>
        </div>
      )}
    </div>
  );
};

export default ToolBar;
